<template>
  <div class="animated fadeIn">
    <b-row>
      <!-- ================= Activation code ================= -->
      <b-col lg="4" class="mb-3">
        <div class="card shadow-sm border-0 h-100">
          <div class="card-header bg-dark text-white py-2">
            <h6 class="mb-0"><font-awesome-icon icon="qrcode" /> Kiosk Activation Code</h6>
          </div>
          <div class="card-body text-center d-flex flex-column">
            <p class="text-muted small text-left">
              Generate a one-time <strong>6-digit code</strong> and type it on the machine that will run the
              Office Time Log kiosk (<code>/office-timelog</code>). The code expires in <strong>3 minutes</strong>
              and works on <strong>one machine only</strong>.
            </p>

            <div v-if="code" class="flex-grow-1 d-flex flex-column justify-content-center">
              <div class="kiosk-code mb-2" :class="code_status === 'Pending' ? 'text-dark' : 'text-muted'">
                {{ code.code.substring(0, 3) }}<span class="mx-2"></span>{{ code.code.substring(3) }}
              </div>

              <template v-if="code_status === 'Pending'">
                <div class="mb-2">
                  <b-badge :variant="remaining_seconds <= 30 ? 'danger' : 'warning'" class="px-3 py-2">
                    <font-awesome-icon icon="clock" /> expires in {{ remaining_label }}
                  </b-badge>
                </div>
                <b-progress :value="remaining_seconds" :max="code.expires_in_seconds" height="6px"
                  :variant="remaining_seconds <= 30 ? 'danger' : 'warning'" class="mb-3"></b-progress>
                <small class="text-muted d-block mb-3">
                  <b-spinner small type="grow" variant="secondary"></b-spinner> Waiting for a machine to activate...
                </small>
                <b-button variant="outline-danger" size="sm" @click="cancelCode">
                  <font-awesome-icon icon="times-circle" /> Cancel this code
                </b-button>
              </template>

              <template v-else-if="code_status === 'Used'">
                <b-alert show variant="success" class="py-2 mb-3">
                  <font-awesome-icon icon="check-circle" />
                  Activated on <strong>{{ used_device_name }}</strong>
                </b-alert>
              </template>
              <template v-else>
                <b-alert show variant="secondary" class="py-2 mb-3">
                  Code {{ code_status.toLowerCase() }}. Generate a new one if still needed.
                </b-alert>
              </template>
            </div>

            <div v-else class="flex-grow-1 d-flex align-items-center justify-content-center text-muted py-4">
              <div>
                <font-awesome-icon icon="lock" class="h1 mb-2" />
                <div>No code on display</div>
              </div>
            </div>

            <b-button variant="success" block class="mt-3" @click="generateCode" :disabled="generating">
              <b-spinner small v-if="generating"></b-spinner>
              <font-awesome-icon icon="plus-circle" v-else />
              {{ code && code_status === 'Pending' ? 'Generate a new code' : 'Generate code' }}
            </b-button>
            <small class="text-muted mt-2" v-if="code && code_status === 'Pending'">Generating a new code cancels the current one.</small>
          </div>
        </div>
      </b-col>

      <!-- ================= Activated machines ================= -->
      <b-col lg="8" class="mb-3">
        <div class="card shadow-sm border-0 h-100">
          <div class="card-header bg-dark text-white d-flex align-items-center justify-content-between py-2">
            <h6 class="mb-0">
              <font-awesome-icon icon="list" /> Activated Machines
              <b-badge variant="success" class="ml-1">{{ active_devices.length }} active</b-badge>
            </h6>
            <div>
              <b-form-checkbox v-model="show_terminated" switch size="sm" class="d-inline-block mr-3 text-white-50">
                show terminated
              </b-form-checkbox>
              <b-button variant="light" size="sm" @click="getDevices" :disabled="loading_devices" title="Refresh">
                <font-awesome-icon icon="check" v-if="!loading_devices" />
                <b-spinner small v-else></b-spinner>
              </b-button>
              <b-button variant="danger" size="sm" class="ml-1" @click="revokeAll" :disabled="active_devices.length === 0">
                <font-awesome-icon icon="times-circle" /> Terminate all
              </b-button>
            </div>
          </div>
          <div class="card-body p-0">
            <b-table
              responsive hover striped small class="mb-0"
              :items="visible_devices" :fields="device_fields" :busy="loading_devices" show-empty head-variant="light"
              empty-text="No machine has been activated yet."
            >
              <template v-slot:cell(name)="data">
                <div class="font-weight-bold">
                  {{ data.item.name }}
                  <b-button variant="link" size="sm" class="p-0 ml-1 text-muted" @click="openRename(data.item)" title="Rename">
                    <font-awesome-icon icon="edit" />
                  </b-button>
                </div>
                <small class="text-muted d-block text-truncate device-fingerprint" :title="data.item.fingerprint">
                  {{ shortFingerprint(data.item.fingerprint) }}
                </small>
              </template>

              <template v-slot:cell(activated)="data">
                <small class="d-block">{{ data.item.formatted_activated_at }}</small>
                <small class="text-muted">by {{ data.item.activated_by_user ? data.item.activated_by_user.full_name : '—' }}</small>
              </template>

              <template v-slot:cell(last_seen)="data">
                <small class="d-block">{{ data.item.last_seen_human }}</small>
                <small class="text-muted" v-if="data.item.last_ip">{{ data.item.last_ip }}</small>
              </template>

              <template v-slot:cell(punches)="data">
                <div class="text-center">
                  <strong>{{ data.item.punches_today }}</strong> <small class="text-muted">today</small>
                  <small class="text-muted d-block">{{ data.item.punches_total }} total</small>
                </div>
              </template>

              <template v-slot:cell(status)="data">
                <div class="text-center">
                  <b-badge :variant="data.item.is_active ? 'success' : 'secondary'">{{ data.item.status }}</b-badge>
                  <small class="text-muted d-block" v-if="!data.item.is_active && data.item.revoked_by_user">
                    by {{ data.item.revoked_by_user.full_name }}<br>{{ data.item.formatted_revoked_at }}
                  </small>
                </div>
              </template>

              <template v-slot:cell(action)="data">
                <div class="text-center">
                  <b-button v-if="data.item.is_active" variant="outline-danger" size="sm" @click="revoke(data.item)" title="Terminate this machine">
                    <font-awesome-icon icon="times-circle" /> Terminate
                  </b-button>
                </div>
              </template>
            </b-table>
          </div>
          <div class="card-footer py-2">
            <small class="text-muted">
              Terminating a machine takes effect on its very next scan: the kiosk returns to the activation screen.
              A terminated machine can only come back with a new code.
            </small>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ================= Code history ================= -->
    <div class="card shadow-sm border-0">
      <div class="card-header bg-dark text-white d-flex align-items-center justify-content-between py-2">
        <h6 class="mb-0"><font-awesome-icon icon="list-ol" /> Activation Code History</h6>
      </div>
      <div class="card-body p-0">
        <b-table
          responsive hover striped small class="mb-0"
          :items="codes.data" :fields="code_fields" :busy="loading_codes" show-empty head-variant="light"
          empty-text="No codes generated yet."
        >
          <template v-slot:cell(created)="data">
            <small class="d-block">{{ data.item.formatted_created_at }}</small>
            <small class="text-muted">by {{ data.item.created_by_user ? data.item.created_by_user.full_name : '—' }}</small>
          </template>
          <template v-slot:cell(expires)="data">
            <small>{{ data.item.formatted_expires_at }}</small>
          </template>
          <template v-slot:cell(status)="data">
            <b-badge :variant="statusVariant(data.item.status)">{{ data.item.status }}</b-badge>
          </template>
          <template v-slot:cell(used)="data">
            <template v-if="data.item.used_by_device">
              <small class="d-block font-weight-bold">{{ data.item.used_by_device.name }}</small>
              <small class="text-muted">{{ data.item.formatted_used_at }}</small>
            </template>
            <small class="text-muted" v-else>—</small>
          </template>
        </b-table>
      </div>
      <div class="card-footer py-2" v-if="codes.total > 0">
        <b-pagination :disabled="loading_codes" @input="getCodes" align="right" size="sm"
          :total-rows="parseInt(codes.total)" :per-page="parseInt(codes.per_page)"
          v-model="codes.current_page" class="mb-0"></b-pagination>
      </div>
    </div>

    <!-- ================= Rename modal ================= -->
    <b-modal v-model="rename_modal" centered size="sm" :hide-footer="true" title="Rename Machine">
      <b-form @submit.prevent="saveRename">
        <b-form-group label="Name">
          <b-form-input v-model="rename_form.name" required maxlength="100" autofocus></b-form-input>
        </b-form-group>
        <b-button type="submit" variant="success" block><font-awesome-icon icon="save" /> Save</b-button>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
  import SwalMixin from '@/views/mixins/Swal.js'

  export default {
    name: 'OfficeKioskDevices',
    mixins: [SwalMixin],
    data() {
      return {
        // code on display
        code: null,                 // { id, code, expires_at, expires_in_seconds }
        code_status: 'Pending',     // Pending | Used | Expired | Cancelled
        used_device_name: '',
        generating: false,
        remaining_seconds: 0,
        code_deadline: null,
        tick_timer: null,
        status_timer: null,

        // devices
        devices: [],
        loading_devices: true,
        show_terminated: false,
        devices_timer: null,
        device_fields: [
          { key: 'name', label: 'Machine' },
          { key: 'activated', label: 'Activated' },
          { key: 'last_seen', label: 'Last Seen' },
          { key: 'punches', label: 'Punches', class: 'text-center' },
          { key: 'status', label: 'Status', class: 'text-center' },
          { key: 'action', label: '', class: 'text-center' },
        ],
        rename_modal: false,
        rename_form: { id: null, name: '' },

        // code history
        codes: { data: [], current_page: 1, total: 0, per_page: 1 },
        loading_codes: true,
        code_fields: [
          { key: 'created', label: 'Generated' },
          { key: 'expires', label: 'Expires' },
          { key: 'status', label: 'Status' },
          { key: 'used', label: 'Activated Machine' },
        ],
      }
    },
    computed: {
      active_devices() {
        return this.devices.filter(d => d.is_active);
      },
      visible_devices() {
        return this.show_terminated ? this.devices : this.active_devices;
      },
      remaining_label() {
        const s = Math.max(0, this.remaining_seconds);
        const m = Math.floor(s / 60);
        const r = s % 60;
        return m + ':' + (r < 10 ? '0' : '') + r;
      }
    },
    created() {
      this.getDevices();
      this.getCodes();
      // Keep "last seen" fresh while the page is open.
      this.devices_timer = setInterval(this.getDevices, 30000);
    },
    beforeDestroy() {
      this.stopCodeTimers();
      clearInterval(this.devices_timer);
    },
    methods: {
      /* ---------------- code ---------------- */
      generateCode() {
        this.generating = true;
        this.axios.post('office-kiosk/generate-code').then(response => {
          this.generating = false;
          this.code = response.data;
          this.code_status = 'Pending';
          this.used_device_name = '';
          this.code_deadline = Date.now() + (response.data.expires_in_seconds * 1000);
          this.remaining_seconds = response.data.expires_in_seconds;
          this.startCodeTimers();
          this.getCodes();
        }).catch(error => {
          this.generating = false;
          this.swal_transaction_error_data.text = (error.response && error.response.data) ? error.response.data : 'Unable to generate a code.';
          this.swalRequestError();
        });
      },
      cancelCode() {
        if (!this.code) return;
        this.axios.post('office-kiosk/cancel-code', { id: this.code.id }).then(() => {
          this.code_status = 'Cancelled';
          this.stopCodeTimers();
          this.getCodes();
        }).catch(error => {
          this.swal_transaction_error_data.text = (error.response && error.response.data) ? error.response.data : 'Unable to cancel the code.';
          this.swalRequestError();
        });
      },
      startCodeTimers() {
        this.stopCodeTimers();
        this.tick_timer = setInterval(() => {
          this.remaining_seconds = Math.max(0, Math.round((this.code_deadline - Date.now()) / 1000));
          if (this.remaining_seconds <= 0 && this.code_status === 'Pending') {
            this.code_status = 'Expired';
            this.stopCodeTimers();
            this.getCodes();
          }
        }, 1000);
        // Poll so the admin sees the machine appear the moment it activates.
        this.status_timer = setInterval(this.pollCodeStatus, 3000);
      },
      stopCodeTimers() {
        clearInterval(this.tick_timer);
        clearInterval(this.status_timer);
        this.tick_timer = null;
        this.status_timer = null;
      },
      pollCodeStatus() {
        if (!this.code) return;
        this.axios.get('office-kiosk/code-status', { params: { id: this.code.id } }).then(response => {
          const status = response.data.status;
          if (status !== 'Pending') {
            this.code_status = status;
            this.used_device_name = response.data.used_by_device ? response.data.used_by_device.name : '';
            this.stopCodeTimers();
            this.getDevices();
            this.getCodes();
          }
        }).catch(() => { /* transient */ });
      },

      /* ---------------- devices ---------------- */
      getDevices() {
        this.loading_devices = true;
        this.axios.get('office-kiosk/devices').then(response => {
          this.devices = response.data;
          this.loading_devices = false;
        }).catch(error => {
          console.log(error);
          this.loading_devices = false;
        });
      },
      shortFingerprint(fp) {
        if (!fp) return '';
        // "platform | WxH | user agent" -> keep it readable
        const parts = fp.split(' | ');
        const ua = parts[2] || '';
        let browser = '';
        if (/Edg\//.test(ua)) browser = 'Edge';
        else if (/OPR\//.test(ua)) browser = 'Opera';
        else if (/Chrome\//.test(ua)) browser = 'Chrome';
        else if (/Firefox\//.test(ua)) browser = 'Firefox';
        else if (/Safari\//.test(ua)) browser = 'Safari';
        return [parts[0], browser, parts[1]].filter(p => p).join(' · ');
      },
      openRename(device) {
        this.rename_form = { id: device.id, name: device.name };
        this.rename_modal = true;
      },
      saveRename() {
        this.axios.post('office-kiosk/devices/rename', this.rename_form).then(() => {
          this.rename_modal = false;
          this.swalTransactionCompleted();
          this.getDevices();
        }).catch(error => {
          this.swal_transaction_error_data.text = (error.response && error.response.data) ? error.response.data : 'Unable to rename.';
          this.swalRequestError();
        });
      },
      revoke(device) {
        this.$swal({
          title: 'Terminate "' + device.name + '"?',
          text: 'The machine will be logged out of the kiosk on its next scan and will need a new activation code to be used again.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          confirmButtonText: 'Yes, terminate',
          cancelButtonText: 'Cancel'
        }).then(result => {
          if (!result.value) return;
          this.axios.post('office-kiosk/devices/revoke', { id: device.id }).then(() => {
            this.swal_transaction_completed_data.text = '"' + device.name + '" terminated.';
            this.swalTransactionCompleted();
            this.getDevices();
          }).catch(error => {
            this.swal_transaction_error_data.text = (error.response && error.response.data) ? error.response.data : 'Unable to terminate.';
            this.swalRequestError();
          });
        });
      },
      revokeAll() {
        this.$swal({
          title: 'Terminate ALL machines?',
          text: 'Every kiosk (' + this.active_devices.length + ') will stop working immediately and any pending activation code will be cancelled.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          confirmButtonText: 'Yes, terminate all',
          cancelButtonText: 'Cancel'
        }).then(result => {
          if (!result.value) return;
          this.axios.post('office-kiosk/devices/revoke-all').then(response => {
            this.swal_transaction_completed_data.text = response.data.terminated + ' machine(s) terminated.';
            this.swalTransactionCompleted();
            if (this.code && this.code_status === 'Pending') {
              this.code_status = 'Cancelled';
              this.stopCodeTimers();
            }
            this.getDevices();
            this.getCodes();
          }).catch(error => {
            this.swal_transaction_error_data.text = (error.response && error.response.data) ? error.response.data : 'Unable to terminate.';
            this.swalRequestError();
          });
        });
      },

      /* ---------------- code history ---------------- */
      getCodes() {
        this.loading_codes = true;
        this.axios.get('office-kiosk/codes', { params: { page: this.codes.current_page, per_page: 10 } }).then(response => {
          this.codes = response.data;
          this.loading_codes = false;
        }).catch(error => {
          console.log(error);
          this.loading_codes = false;
        });
      },
      statusVariant(status) {
        switch (status) {
          case 'Pending': return 'warning';
          case 'Used': return 'success';
          case 'Cancelled': return 'secondary';
          default: return 'light';
        }
      }
    }
  }
</script>

<style scoped>
  .kiosk-code {
    font-size: 3.2rem;
    font-weight: 800;
    letter-spacing: 0.35rem;
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }
  .device-fingerprint {
    max-width: 260px;
  }
</style>
